import { useSearchProductsQuery } from '@/features/navigation/api/query/search-products';
import { ProductNotFound } from '@/features/navigation/components/search/product-not-found';
import { categorizeProducts } from '@/features/navigation/hooks/categorize-products';
import { clsx } from 'clsx';
import { type Control, Controller } from 'react-hook-form';
import { CategoriesList } from './categories-list';
import { ProductsList } from './products-list';
import type { SearchFormValues } from './search';
import { SearchInput } from './search-input';

const { open, closed } = {
  //96px = (top-bottom padding + top-bottom absolute)
  open: 'absolute z-top box-content h-max w-full rounded-l left-[-24px] bg-main p-m -top-m max-h-[calc(100vh-96px)] overflow-hidden',
  closed: 'relative z-1 p-0 w-full h-max',
};

interface Props {
  handleChangeQuery: (query: string) => void;
  reset: () => void;
  debouncedQuery: string;
  isDesktopSearchExpanded: boolean;
  control: Control<SearchFormValues>;
  toggleFocus: (focused: boolean) => void;
}

export const DesktopSearch = ({
  debouncedQuery,
  handleChangeQuery,
  reset,
  isDesktopSearchExpanded,
  control,
  toggleFocus,
}: Props) => {
  const { data, isLoading } = useSearchProductsQuery(debouncedQuery);

  const { categories, products } = categorizeProducts(data?.hits ?? []);

  return (
    <div
      className={clsx(
        'relative z-500 l:mr-xl mr-m l:ml-0 ml-l m:flex hidden h-clickable l:w-full w-full group-[.sticky]:xxl:mr-0 group-[.sticky]:h-clickable-minimum group-[.sticky]:l:w-full group-[.sticky]:l:max-w-full group-[.sticky]:m:max-w-[534px]',
      )}
    >
      <div
        onClick={reset}
        className={clsx(
          'fixed top-0 left-0 z-100 h-screen w-screen bg-overlay backdrop-blur-sm',
          isDesktopSearchExpanded ? 'block' : 'hidden',
        )}
      />
      <div className={clsx(isDesktopSearchExpanded ? open : closed)}>
        <Controller
          control={control}
          name="query"
          render={({ field: { onBlur, onChange, value } }) => (
            <SearchInput
              className="group-[.sticky]:h-clickable-minimum"
              iconClassName="group-[.sticky]:!h-clickable-minimum"
              aria-expanded={isDesktopSearchExpanded}
              onBlur={(e) => {
                onBlur();
                handleChangeQuery(e.target.value);
                toggleFocus(false);
              }}
              onChange={(e) => {
                onChange(e);
                handleChangeQuery(e.target.value);
              }}
              onFocus={() => {
                toggleFocus(true);
              }}
              onReset={reset}
              value={value}
            />
          )}
        />
        {isDesktopSearchExpanded && (
          //168px = 96px (top-bottom padding + top-bottom absolute) + 48px (search input) + 24px (product wrapper wrapper margins)
          <div
            className="mt-xs max-h-[calc(100vh-168px)] overflow-y-auto"
            hidden={!isDesktopSearchExpanded}
            aria-live="polite"
          >
            <ProductsList products={products} isLoading={isLoading} />
            <CategoriesList categories={categories} isLoading={isLoading} />
            {data && data?.hitsCount <= 0 && <ProductNotFound />}
          </div>
        )}
      </div>
    </div>
  );
};
